import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Switch, useHistory, useLocation } from 'react-router-dom';

import StaticFooter from './shared/layout/StaticFooter';
import StaticHeader from './shared/layout/StaticHeader';
import Loading from './shared/Loading';
import {
  init,
  loading,
  setIsModal,
  setMode,
  setPublicKey,
} from '../actions/sessions';
import { fromLearnMore, workflows } from '../App';
import useSessionEntity from '../hooks/useSessionEntity';
import {
  platform,
  subDomains,
  valuePropsExcludedFromModes,
} from '../lib/constants/SiteVariables';
import { captureCheckoutEvent } from '../lib/utils/checkout_events';
import { merchantIsCreditKey } from '../lib/utils/merchant_restricted_text';
import { emitEvent, setHeight, updateParent } from '../lib/utils/modal';
import { navigateTo } from '../lib/utils/navigation';
import PostHog from '../lib/utils/posthog';
import { shouldUseSectionCardLayout } from '../lib/utils/terms';

export default function Session(props) {
  const history = useHistory();
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.sessions.get('loading'));
  const session = useSelector((state) => state.sessions.get('session'));
  const handoffUrl =
    session.pay_now_handoff && session.pay_now_handoff.get('checkout_url');
  const application = useSessionEntity('application');
  const cart = useSessionEntity('shopping_cart');
  const company = useSessionEntity('company');
  const user = useSessionEntity('user');
  const merchant = useSessionEntity('merchant');
  const [loaded, setLoaded] = useState(false);
  const [curPath, setCurPath] = useState();
  const { pathname, search } = useLocation();

  const isOnLandingPage = pathname.startsWith(
    '/init/new_business_qualifications'
  );
  const isOnTermsPage = pathname.startsWith('/purchase/terms');
  const isSectionCardTermsPage =
    isOnTermsPage && shouldUseSectionCardLayout(cart, company);
  const useWideContainer =
    !valuePropsExcludedFromModes.includes(session.mode) && isOnLandingPage;

  const getRedirectPath = (session) => {
    let redirect = {
      pathname: '/' + session.workflow + '/' + session.action,
      state: '',
    };

    const isNewUserApplyNow =
      session.session_type === 'apply_now' &&
      ['apply_now', 'new_user'].includes(session.action);

    const isRechargeOrInstore =
      session.custom_flags.includes('instore') ||
      session.custom_flags.includes('recharge');

    if (merchant && merchant.id && merchantIsCreditKey(merchant)) {
      return (redirect.pathname = '/invalid/merchant');
    }

    if (
      isNewUserApplyNow &&
      document.referrer.replace(/\/$/, '') !== process.env.REACT_APP_PI4 &&
      !fromLearnMore &&
      !isRechargeOrInstore
    ) {
      redirect.pathname = '/init/apply_now';
      redirect.state = { redirect_url: '/init/new_user' };
    }
    return redirect;
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    const path = pathname.split('/')[2];

    if (
      loaded &&
      merchant &&
      merchant.name !== '' &&
      path &&
      (path !== curPath || (path && !curPath))
    ) {
      setCurPath(path);
      captureCheckoutEvent(path.replace(/_/g, ''), {
        application,
        company,
        merchant,
        session,
        user,
        shopping_cart: cart,
      });

      if (session && session.mode === 'modal') {
        emitEvent(user, path, cart);
      }
    }
  }, [pathname, merchant, loaded]);

  useEffect(() => {
    if (!loaded || user?.id) return;

    const posthog = new PostHog();
    posthog.resetStaleIdentity();
  }, [loaded, user]);

  useEffect(() => {
    if (!merchant || merchant.name === '') return;

    const posthog = new PostHog();

    posthog.identify({
      application,
      company,
      merchant,
      session,
      user,
    });
    posthog.group({ company, merchant });
  }, [application, company, merchant, session, user]);

  useEffect(() => {
    const content = document.getElementById('content');

    // add height of header + footer + css spacing
    content &&
      !isLoading &&
      (!session.mode || session.mode === 'modal') &&
      setHeight(content.clientHeight + 305);
  }, [isLoading, pathname]);

  useEffect(() => {
    dispatch(setPublicKey(props.match.params));
  }, [props.match]);

  useEffect(() => {
    let redirectTo = '/init/existing_user';

    dispatch(init())
      .then(() => setLoaded(true))
      .catch((err) => {
        const response = err?.response;
        const data = response?.data;
        if (
          response?.status === 409 &&
          data?.outcome === 'retired' &&
          data?.reason_code === 'handed_off_to_pay_now' &&
          typeof data.checkout_url === 'string' &&
          data.checkout_url.trim()
        ) {
          // Authentication rejects retired sources before their session can load.
          navigateTo(data.checkout_url);
          return;
        }

        setLoaded(true);

        if (subDomains.authorized()) {
          const [merchant_key, prequal_id, params] = subDomains.parseParams(
            props.match.params
          );

          // TODO: cleanup? I think this "modal" thing comes from our SDK / pi4 UI; could use "mode=modal" now
          if (params.get('modal')) dispatch(setIsModal(!!params.get('modal')));
          if (params.get('mode')) dispatch(setMode(params.get('mode')));

          if (!merchant_key) {
            redirectTo = '/invalid';
          } else {
            // update the public_key again to make sure we pick up any change
            dispatch(setPublicKey({ public_key: merchant_key }));
            redirectTo = `/apply/${merchant_key}/${prequal_id}${search}`;
          }
        }

        if (merchant && merchant.isOnHold) redirectTo = '/error';

        history.replace(redirectTo);
        dispatch(loading(false));
      });
  }, []);

  useEffect(() => {
    if (handoffUrl) navigateTo(handoffUrl);
  }, [handoffUrl]);

  useEffect(() => {
    if (handoffUrl) return;

    if (merchant && merchant.isOnHold) return history.push('/error');

    if (merchant && session.workflow) {
      history.push(getRedirectPath(session));
    }

    if (session.disposition === 'completed' && company) {
      history.push(`/finish/${company.underwriting_status}`);
    }
  }, [merchant, session.workflow, session.action, handoffUrl]);

  useEffect(() => {
    if (platform() !== 'development' && session.public_key) {
      window.ckLoadZendeskWidget();
    }
  }, [session]);

  const dismiss = () => {
    if (!session.mode || session.mode === 'modal') {
      // detect if iframed
      if (window.self !== window.top) {
        return updateParent('cancel', null);
      } else {
        window.history.back();
        return true;
      }
    }

    if (session.mode !== 'modal') {
      if (cart && session.session_type === 'standalone')
        window.location = merchant.url;

      cart && cart.cancel_url
        ? (window.location = cart.cancel_url)
        : (window.location = merchant.url);
    }
  };

  if (handoffUrl) return null;

  return (
    <div className="flex flex-col min-h-screen xxs:h-full font-manrope">
      <StaticHeader
        isLoading={isLoading}
        merchant={merchant}
        dismiss={dismiss}
        session={session}
      />
      <div
        className={`flex-grow flex items-start overflow-auto sm:pt-10 bg-[#fcfcfc] ${
          isSectionCardTermsPage
            ? 'md:h-[calc(100vh-64px)] md:overflow-y-auto'
            : ''
        }`}
      >
        <div
          className={`mx-auto ${
            useWideContainer || isSectionCardTermsPage
              ? 'max-w-5xl'
              : 'max-w-xl'
          } w-full`}
        >
          {!loaded && (
            <Loading
              className="h-14 w-14 mx-auto"
              containerClass="min-h-[55vh] my-14"
            />
          )}
          {loaded && (
            <div
              id="content"
              data-testid="session"
              className="min-h-[65vh] w-full my-6 md:my-0 px-1"
            >
              <Switch>
                {workflows.map((w) => (
                  <Route key={w.path} path={w.path} component={w.component} />
                ))}
              </Switch>
            </div>
          )}
        </div>
      </div>
      <StaticFooter />
    </div>
  );
}
